/**
 * Supported locale codes (`locale-registry.json`). Vendored into the website repo so
 * Docker/CI builds work without the monorepo parent checkout.
 */
import registry from "./locale-registry.json" with { type: "json" };

/** @param {unknown} list */
function freezeList(list) {
  return Object.freeze([...(Array.isArray(list) ? list : [])]);
}

export const UI_LANGS = freezeList(registry.uiLangs);
export const CONTENT_I18N_LANGS = freezeList(registry.contentI18nLangs);
export const TARGET_LANGS = freezeList(registry.targetLangs);
export const DEFAULT_FALLBACK_LANG = "de";
export const CATALOG_LANG_FALLBACK = String(registry.catalogLangFallback || DEFAULT_FALLBACK_LANG);
export const FLEXIBLE_DISPLAY_FALLBACK_LANGS = freezeList(registry.flexibleDisplayFallbackLangs);

/** @type {ReadonlySet<string>} */
export const UI_LANG_SET = new Set(UI_LANGS);
/** @type {ReadonlySet<string>} */
export const CONTENT_I18N_LANG_SET = new Set(CONTENT_I18N_LANGS);

/**
 * @param {string} [code]
 * @returns {string}
 */
export function normalizeLocaleCode(code) {
  return String(code || "")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
}

/**
 * @param {string} [code]
 * @returns {boolean}
 */
export function isUiLang(code) {
  return UI_LANG_SET.has(normalizeLocaleCode(code));
}

/**
 * @param {string} [code]
 * @returns {boolean}
 */
export function isContentI18nLang(code) {
  return CONTENT_I18N_LANG_SET.has(normalizeLocaleCode(code));
}

/**
 * Row-oriented catalog field (`pos.json`, `exercises-ui.json`, …) for a UI language.
 *
 * @param {string} [uiLang]
 * @returns {string}
 */
export function catalogLangForUi(uiLang) {
  const u = normalizeLocaleCode(uiLang);
  if (u === "ch") return "de";
  if (CONTENT_I18N_LANG_SET.has(u)) return u;
  return CATALOG_LANG_FALLBACK;
}

/**
 * Deduped fallback chain for `resolveFlexibleDisplayText` and similar lookups.
 *
 * @param {...string} preferred
 * @returns {string[]}
 */
export function flexibleDisplayFallbackChain(...preferred) {
  /** @type {string[]} */
  const out = [];
  /** @type {Set<string>} */
  const seen = new Set();
  for (const raw of [...preferred, ...FLEXIBLE_DISPLAY_FALLBACK_LANGS]) {
    const code = normalizeLocaleCode(raw);
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}
